import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { RevealLines, FadeUp } from "@/components/site/Reveal";
import { CATEGORIES, CATEGORY_LABELS } from "@/data/site";
import api from "@/lib/api";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/portfolio")
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visible = filter === "all" ? items : items.filter((p) => p.category === filter);

  return (
    <div data-testid="portfolio-page">
      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 pt-40 lg:pt-52 pb-12">
        <p className="mb-8 flex items-center gap-3 text-sm tracking-[0.25em] uppercase text-muted-foreground">
          <span className="h-px w-10 bg-violet" /> Portfolio
        </p>
        <h1 className="font-display font-extrabold tracking-tighter leading-[0.9] text-6xl sm:text-7xl lg:text-8xl">
          <RevealLines lines={["Nos", "réalisations."]} />
        </h1>

        {/* Filters */}
        <FadeUp delay={0.3}>
          <div className="mt-14 flex flex-wrap gap-3" data-testid="portfolio-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                data-testid={`filter-${c.key}`}
                className={`px-5 py-2.5 text-sm border transition-colors duration-300 ${
                  filter === c.key
                    ? "bg-ink text-white border-ink"
                    : "border-ink/20 text-ink/70 hover:border-violet hover:text-violet"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 pb-28">
        {loading ? (
          <p className="text-muted-foreground py-20">Chargement…</p>
        ) : visible.length === 0 ? (
          <p className="text-muted-foreground py-20" data-testid="portfolio-empty">
            Aucun projet dans cette catégorie pour le moment.
          </p>
        ) : (
          <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="portfolio-grid">
            <AnimatePresence mode="popLayout">
              {visible.map((p) => (
                <motion.article
                  key={p.id}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="group"
                  data-testid={`project-${p.id}`}
                >
                  <div className="relative overflow-hidden aspect-[4/3] bg-secondary">
                    <img
                      src={p.image_url}
                      alt={p.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 text-xs tracking-wide uppercase">
                      {CATEGORY_LABELS[p.category]}
                    </span>
                    {p.link ? (
                      <a
                        href={p.link}
                        target="_blank"
                        rel="noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-ink/0 group-hover:bg-ink/40 transition-colors"
                        aria-label={p.title}
                      >
                        <ArrowUpRight className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={36} />
                      </a>
                    ) : null}
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display font-bold text-xl tracking-tight">{p.title}</h3>
                      {p.client && <p className="text-muted-foreground text-sm mt-1">{p.client}</p>}
                    </div>
                    <span className="font-editorial text-muted-foreground shrink-0">{p.year}</span>
                  </div>
                  {p.description && <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.description}</p>}
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </section>
    </div>
  );
}
